import { Key } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { formatRelativeTime } from "~/foundation/utils/time";
import { usePolicy } from "~/policies/usePolicy";

function KeyRow({
  apiKey,
  onRevoke,
  busy,
}: {
  apiKey: { id: string; name: string; partialSecret: string; lastActiveAt: string | null };
  onRevoke: (id: string) => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const can = usePolicy(apiKey.id);

  return (
    <li className="flex items-center gap-3 border-b border-divider px-3 py-2.5 last:border-b-0">
      <Key size={18} aria-hidden className="shrink-0 text-text-tertiary" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">{apiKey.name}</p>
        <p className="truncate font-mono text-small text-text-tertiary">{apiKey.partialSecret}</p>
      </div>
      <span className="hidden text-small text-text-tertiary sm:inline">
        {apiKey.lastActiveAt
          ? formatRelativeTime(apiKey.lastActiveAt, i18n.language)
          : t("Never used")}
      </span>
      {can.revoke ? (
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onRevoke(apiKey.id)}>
          {t("Revoke")}
        </Button>
      ) : null}
    </li>
  );
}

/** Serves both the personal and the workspace key sections — same shape, same gate. */
export function ApiKeys() {
  const { t } = useTranslation();
  const { data: keys, isLoading } = useApiKeys();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();
  const [name, setName] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    createKey.mutate(
      { name: name.trim() },
      {
        onSuccess: (key) => {
          setName("");
          toast.success(t("Created “{{title}}”", { title: key.name }));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} className="flex items-end gap-2">
        <Input
          label={t("New key name")}
          value={name}
          fullWidth
          placeholder={t("CI pipeline")}
          onChange={(event) => setName(event.target.value)}
        />
        <Button type="submit" variant="primary" disabled={createKey.isPending || !name.trim()}>
          {t("Create")}
        </Button>
      </form>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !keys?.length ? (
        <Empty icon={Key} message={t("No API keys yet")} />
      ) : (
        <ul className="flex flex-col rounded-md border border-divider">
          {keys.map((apiKey) => (
            <KeyRow
              key={apiKey.id}
              apiKey={apiKey}
              busy={revokeKey.isPending}
              onRevoke={(id) =>
                revokeKey.mutate(id, { onSuccess: () => toast.success(t("Key revoked")) })
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}
